import React from "react";
import { classNames } from "../../utils/classNames";
import { HintSurfaceContext } from "./Kbd";

/**
 * The dark chrome pill that in-world hint clusters render into — the
 * player's verb hints, a targeted machine's chips, the door's "head out".
 * Two columns: key caps right-aligned against a single left edge that
 * every label starts from, so a stack of hints reads as a list of verbs
 * rather than a ragged pile. Rows go in as `<HintRow>`; a row with no
 * keys (a title, a note) spans both columns — and both columns size to
 * their content rather than the label column taking a `1fr`, because a
 * flexible track swallows a spanning row's width contribution and a long
 * title (a machine's name) would then hang out past the pill's edge.
 * Wraps its rows in the chrome
 * surface so key caps and muted text stay readable on the dark background
 * (see Kbd.tsx).
 */
export const HintList: React.FC<{
  children: React.ReactNode;
  /** Names the cluster for the specs — the labels inside are single verbs
   * now, too generic to locate a chip by. */
  testId?: string;
}> = ({ children, testId }) => (
  <HintSurfaceContext.Provider value="chrome">
    <ul
      data-testid={testId}
      className="grid grid-cols-[auto_auto] justify-start items-baseline gap-x-1.5 gap-y-0.5 rounded bg-ink-black/70 px-2 py-1 text-left font-condensed text-[0.65rem] uppercase tracking-[0.1em] text-paper-manila/90 whitespace-nowrap"
    >
      {children}
    </ul>
  </HintSurfaceContext.Provider>
);

/**
 * The dress a "here's why not" wears: muted, sentence-case italic so it
 * reads as a note rather than a control, and allowed to wrap since a
 * reason can run longer than a verb. Shared by the reason under a dimmed
 * verb (`HintRow`'s `reason`) and the stand-alone `ReasonRow`, so every
 * explanation reads alike.
 */
const reasonClass =
  "max-w-56 whitespace-normal normal-case italic tracking-normal text-paper-manila/70";

/**
 * One row of a hint cluster: its key caps in the left column, what they do
 * in the right. The `<li>` is `display: contents` so both cells sit
 * directly in the list's grid and line up with every other row's;
 * `className` still styles both, since colour inherits through it.
 *
 * `hold` marks a key that has to be held down rather than tapped. It rides
 * the key column, not the label — "hold" describes the press, so the label
 * column stays a plain list of verbs ("sweep", "rip") instead of every
 * held key spending three words on "hold to ..." before saying anything.
 *
 * `reason` is why the key won't answer right now ("arms full"). The row
 * stays put — key cap and verb dimmed, so the reader still sees what the
 * key does here and that it's off — and the reason follows on its own
 * line under the verb. A reason never replaces the verb: "arms full" on
 * its own says nothing about what you'd be able to do once they weren't.
 */
export const HintRow: React.FC<{
  keys?: React.ReactNode;
  hold?: boolean;
  reason?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}> = ({ keys, hold, reason, className, children }) => {
  if (keys == null) {
    return <li className={classNames("col-span-2", className)}>{children}</li>;
  }
  const off = reason != null;
  return (
    <>
      <li className={classNames("contents", className)}>
        {/* Dimmed per cell: a `contents` element has no box of its own
            for opacity to apply to */}
        <span
          className={classNames(
            "justify-self-end whitespace-nowrap",
            off && "opacity-40",
          )}
        >
          {hold && (
            <span className="mr-1 text-[0.6rem] text-paper-manila/50">
              hold
            </span>
          )}
          {keys}
        </span>
        <span className={off ? "opacity-40" : undefined}>{children}</span>
      </li>
      {off && (
        <li className="contents" data-testid="hint-reason">
          <span />
          <span className={reasonClass}>{reason}</span>
        </li>
      )}
    </>
  );
};

/**
 * A key-less row explaining why something isn't on offer right now, where
 * there's no verb row to hang the reason under (the feed-refusal advice
 * in MachineChips, which is about the setup rather than one key). Where
 * the blocked key has a verb, dim that row with `HintRow`'s `reason`.
 */
export const ReasonRow: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <HintRow className={reasonClass}>{children}</HintRow>;
